import { useState } from 'react';
import BasicInfoItem from '../create-proposal/steps/fourth-step/review-proposal/basic-info-item';
import { CHANNEL_TYPE_ID } from '../../../constant/gms-constant';

interface ChannelData {
    channelTypeId?: number | string | null;
    channelTypeName?: string | null;
    channelSubName?: string | null;
    channelSubNumber?: string | number | null;
    channelSubEmail?: string | null;
}

interface Props {
    isShow: boolean;
    current: ChannelData;
    proposed: ChannelData;
}

const normalizeValue = (value: unknown): string => {
    if (value === null || value === undefined) {
        return '';
    }

    return String(value).trim();
};

const isDifferent = (current: unknown, proposed: unknown): boolean => {
    return normalizeValue(current) !== normalizeValue(proposed);
};

const getChannelTypeName = (
    channelTypeId: unknown,
    fallbackName = '',
): string => {
    const id = Number(channelTypeId);

    if (id === CHANNEL_TYPE_ID.direct) {
        return 'Direct';
    }

    if (id === CHANNEL_TYPE_ID.broker) {
        return 'Broker';
    }

    if (id === CHANNEL_TYPE_ID.generalAgency) {
        return 'General Agency';
    }

    if (id === CHANNEL_TYPE_ID.agent) {
        return 'Agent';
    }

    return normalizeValue(fallbackName);
};

const isChannelInputVisible = (channelTypeId: unknown): boolean => {
    const id = Number(channelTypeId);

    return (
        id === CHANNEL_TYPE_ID.broker ||
        id === CHANNEL_TYPE_ID.generalAgency ||
        id === CHANNEL_TYPE_ID.agent
    );
};

export default function ProposedAmendChannelType({ isShow, current, proposed, }: Props) {
    const [showContent, setShowContent] = useState(true);

    if (isShow) {
        return null;
    }

    // ============================================================
    // CURRENT
    // ============================================================

    const currentChannelTypeName = getChannelTypeName(current.channelTypeId, current.channelTypeName ?? '');
    const currentShowInput = isChannelInputVisible(current.channelTypeId,);
    // ============================================================
    // PROPOSED
    // ============================================================

    const proposedChannelTypeName = getChannelTypeName(proposed.channelTypeId, proposed.channelTypeName ?? '',);
    const proposedShowInput = isChannelInputVisible(proposed.channelTypeId,);
    const proposedChannelSubName = normalizeValue(proposed.channelSubName,);
    const proposedChannelSubNumber = normalizeValue(proposed.channelSubNumber,);
    const proposedChannelSubEmail = normalizeValue(proposed.channelSubEmail,);

    // ============================================================
    // CHANGES
    // ============================================================

    const channelTypeChanged = isDifferent(current.channelTypeId, proposed.channelTypeId,);

    const channelNameChanged =
        currentShowInput !== proposedShowInput || 
        (currentShowInput && proposedShowInput && 
            isDifferent(current.channelSubName, proposed.channelSubName));

    const channelNumberChanged =
        currentShowInput !== proposedShowInput ||
        (currentShowInput && proposedShowInput && 
            isDifferent( current.channelSubNumber, proposed.channelSubNumber));

    const channelEmailChanged =
        currentShowInput !== proposedShowInput ||
        (currentShowInput && proposedShowInput && 
            isDifferent( current.channelSubEmail, proposed.channelSubEmail));

    return (
        <div className="grid grid-cols-1 gap-3.5">
            <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                {/* SECTION HEADER */}
                <button
                    type="button"
                    onClick={() => setShowContent((previous) => !previous)}
                    className="flex w-full items-center justify-between gap-3 border-b border-slate-200 bg-white px-4 py-2.5 text-left transition-colors hover:bg-slate-50"
                >
                    <div className="flex min-w-0 items-center gap-2">
                        <svg viewBox="0 0 24 24" fill="none" className={`h-3.5 w-3.5 shrink-0 text-[#186cb2] transition-transform ${showContent ? 'rotate-0' : '-rotate-90'}`} aria-hidden="true" >
                            <path d="m7 10 5 5 5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <h3 className="text-[11px] font-semibold uppercase tracking-wide text-slate-700">
                            Channel Type
                        </h3>
                    </div>
                    <span className="shrink-0 text-[9px] font-medium text-slate-400">
                        {showContent ? 'Hide' : 'Show'}
                    </span>
                </button>
                {showContent && (
                    <>
                        {/* CURRENT / PROPOSED HEADER */}
                        <div className="grid grid-cols-1 border-b border-slate-200 sm:grid-cols-2">
                            <div className="border-b border-slate-200 bg-slate-50 px-4 py-2.5 sm:border-b-0 sm:border-r">
                                <h3 className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                    Current
                                </h3>
                            </div>
                            <div className="bg-[#f5f9fc] px-4 py-2.5">
                                <h3 className="text-[11px] font-semibold uppercase tracking-wide text-[#186cb2]">
                                    Proposed
                                </h3>
                            </div>
                        </div>
                        {/* MAIN CONTENT */}
                        <div className="grid grid-cols-1 sm:grid-cols-2">
                            {/* CURRENT */}
                            <div className="min-w-0 border-b border-slate-200 p-4 sm:border-b-0 sm:border-r">
                                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                    {channelTypeChanged && (
                                        <BasicInfoItem
                                            label="Channel Type"
                                            value={currentChannelTypeName}
                                        />
                                    )}
                                    {channelNameChanged && currentShowInput && (
                                        <BasicInfoItem
                                            label={`${currentChannelTypeName || 'Channel'} Name`}
                                            value={normalizeValue(current.channelSubName)}
                                        />
                                    )}
                                    {channelNumberChanged && currentShowInput && (
                                        <BasicInfoItem
                                            label={`${currentChannelTypeName || 'Channel'} Number`}
                                            value={normalizeValue(current.channelSubNumber)}
                                        />
                                    )}
                                    {channelEmailChanged && currentShowInput && (
                                        <BasicInfoItem
                                            label={`${currentChannelTypeName || 'Channel'} Email`}
                                            value={normalizeValue(current.channelSubEmail)}
                                        />
                                    )}
                                </div>
                            </div>
                            {/* PROPOSED */}
                            <div className="min-w-0 p-4">
                                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                    {channelTypeChanged && (
                                        <BasicInfoItem
                                            label="Channel Type"
                                            value={proposedChannelTypeName}
                                        />
                                    )}
                                    {channelNameChanged && proposedShowInput && (
                                        <BasicInfoItem
                                            label={`${proposedChannelTypeName || 'Channel'} Name`}
                                            value={proposedChannelSubName}
                                        />
                                    )}
                                    {channelNumberChanged && proposedShowInput && (
                                        <BasicInfoItem
                                            label={`${proposedChannelTypeName || 'Channel'} Number`}
                                            value={proposedChannelSubNumber}
                                        />
                                    )}
                                    {channelEmailChanged && proposedShowInput && (
                                        <BasicInfoItem
                                            label={`${proposedChannelTypeName || 'Channel'} Email`}
                                            value={proposedChannelSubEmail}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}